import Link from "next/link";
import { Container, Row, Col } from "react-bootstrap";
import { FaArrowLeft } from "react-icons/fa";
import { LayoutOne } from "@/layouts";
import BlogItemTwo from "@/components/blog/blogItemTwo";
import BlogSideBar from "@/components/blog/sidebar";
import SeoHead from "@/components/SeoHead";
import PageViewBeacon from "@/components/PageViewBeacon";
import OurGroupSection from "@/components/ourGroup";
import { getTags, getPosts, getCategories, getAuthors } from "@/lib/cms";
import { getLatestBlogs } from "@/lib/blog";
import { buildTaxonomySchema } from "@/lib/taxonomySchema";

export async function getStaticPaths() {
  const tags = await getTags();
  return {
    paths: tags.map((t) => ({ params: { slug: t.slug } })),
    fallback: "blocking",
  };
}

export async function getStaticProps({ params }) {
  const slug = String(params.slug || "");
  const [tags, categories, latest, authors] = await Promise.all([
    getTags(),
    getCategories(),
    getLatestBlogs(4),
    getAuthors(),
  ]);
  const matched = tags.find((t) => t.slug === slug) || null;
  const { data: posts } = await getPosts({ tag: slug, perPage: 100 });
  return {
    props: {
      tag: matched,
      slug,
      posts,
      latest,
      categories,
      tags,
      authors,
    },
    revalidate: 3600,
  };
}

function TagPage({ tag, slug, posts = [], latest = [], categories = [], tags = [], authors = [] }) {
  const displayName = tag ? tag.name : slug;
  const seo = {
    seo_title: `${displayName} — Blog Posts`,
    meta_description: `Latest blog posts tagged ${displayName}.`,
    focus_keyword: displayName,
    schema_json: buildTaxonomySchema({
      kind: "tag",
      slug,
      displayName,
      description: `Latest blog posts tagged ${displayName}.`,
    }),
  };
  return (
    <>
      <PageViewBeacon contentType="tag" slug={slug} path={`/tag/${slug}`} />
      <LayoutOne topbar={true} banner={true} showHeading={false}>
      <SeoHead
        seo={seo}
        type="website"
        path={`/tag/${slug}`}
        fallbackTitle={displayName}
        fallbackDescription={seo.meta_description}
        extraKeywords={[displayName]}
        publisherName="Siddhivinayak Group"
      />
      <div className="ltn__blog-area mb-120">
        <Container>
          <div className="section-title-area text-center mb-40">
            <h1 className="page-heading-title">
              Tag: {displayName}
              <span className="text-muted fs-6 ms-2">
                ({posts.length} {posts.length === 1 ? "post" : "posts"})
              </span>
            </h1>
            <p className="mb-0">
              <Link href="/blog" className="ltn__back-link">
                <FaArrowLeft /> Back to all posts
              </Link>
            </p>
          </div>
          <Row>
            <Col xs={12} lg={8}>
              <Row className="blog-card-grid">
                {posts.length ? (
                  posts.map((blog, key) => (
                    <Col key={key} xs={12} md={6}>
                      <BlogItemTwo
                        blogData={blog}
                        slug={blog.slug}
                        baseUrl="blog"
                      />
                    </Col>
                  ))
                ) : (
                  <Col xs={12}>
                    <div className="text-center py-5">
                      <h3 className="mb-3">No posts with this tag</h3>
                      <p className="mb-4">
                        We couldn't find any blog posts tagged{" "}
                        <strong>{displayName}</strong>.
                      </p>
                      <Link href="/blog" className="btn theme-btn-1">
                        <FaArrowLeft /> Back to all posts
                      </Link>
                    </div>
                  </Col>
                )}
              </Row>
            </Col>

            <Col xs={12} lg={{ span: 4, order: 0 }}>
              <BlogSideBar
                latestdBlogs={latest}
                categories={categories}
                tags={tags}
                authors={authors}
                showAgent={false}
                showBookForm={false}
                showTopRated={false}
                showAuthors={false}
                showTwitter={false}
                showFollowUs={false}
                activeTag={tag ? tag.name : null}
              />
            </Col>
          </Row>
        </Container>
      </div>
      <div className="section-bg-1">
        <OurGroupSection />
      </div>
    </LayoutOne>
    </>
  );
}

export default TagPage;
